import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'

import classnames from 'classnames'
import { Form, Input, Select, Button } from 'antd'
import ChatLayout from '@/components/ChatLayout'
import useReducers from '@/store/long-creation'
import useUserReducers from '@/store/user'
import http from '@/utils/request'
import topup from '@/utils/topup'
import TextRender from '@/components/TextRender'
import Expense from '@/components/Expense'
import LongCreationDemo from '@/components/Demo/LongCreationDemo'
import tryParse from '@/utils/tryParse'

import TitleInput from './components/TitleInput'
import Article from './components/Article'
import Outline from './components/Outline'

import ArticlePrompt from '@/components/ArticlePrompt'
import styles from './long-creation.module.scss'

export default React.memo(function Creation() {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [userState] = useUserReducers()
  const [state, actions] = useReducers()
  const lastRef = useRef()

  const messages = useMemo(() => {
    return state.messages[state.active] || []
  }, [state.messages, state.active])

  useEffect(() => {
    if (messages.length === 0) {
      form.resetFields()
      return
    }

    const last = messages[messages.length - 1]
    const { extra } = last
    form.setFieldsValue({
      ...extra,
      model: extra?.model || 'gpt-3.5'
    })
  }, [messages])

  useEffect(() => {
    setTimeout(() => {
      if (lastRef.current) {
        lastRef.current.scrollIntoView()
      }
    }, 200)
  }, [messages])

  useEffect(() => {
    if (!state.active) {
      return
    }

    const chatId = state.active

    http.get(`/api/chat/${state.active}`).then(res => {
      if (!res) {
        return
      }

      actions.setMessages({
        [chatId]: res
      })
    })
  }, [state.active])

  const submit = useCallback(async ({ content, ...extra }) => {
    const chatId = state.active
    const newMessages = [
      ...messages, {
      content: content,
      role: 'user',
      id: Math.random()
    }]

    actions.setMessages({
      [chatId]: [...newMessages, {
        content: '正在回复...',
        role: 'assistant',
        id: Math.random(),
      }]
    })

    try {
      const postMessages = messages.slice(-2)
      await http.post('/api/chat/creation/create', {
        chatId: state.active,
        extra: extra,
        messages: [ ...postMessages, { content: content, role: 'user'}],
      }, {
        onDownloadProgress: function ({ event }) {
          const xhr = event.target
          const { responseText } = xhr
          if (xhr.status !== 200) {
            return
          }

          const jsonText = `[${responseText.replace(/\}\{/g, '},{')}]`
          const json = tryParse(jsonText)

          if (!json) {
            return
          }

          const creating = json.reduce((result, next) => {
            return `${result}${next.data}`
          }, '')

          const lastMessage = {
            content: creating,
            role: 'assistant',
            id: Math.random()
          }

          actions.setMessages({
            [chatId]: [...newMessages, lastMessage]
          })
        },
      })

      const chatDetail = await http.get(`/api/chat/${chatId}`)

      actions.setMessages({
        [chatId]: chatDetail
      })
    } catch(err) {
      actions.setMessages({ [chatId]: messages })
      if (err.response?.data.code === '1001') {
        topup()
      }
    }

  }, [messages, state.active])

  return (
    <ChatLayout
      type="long-creation"
      title="长文创作"
      onActiveChange={actions.setActive}
      histories={state.histories}
      active={state.active}
      actions={actions}
    >
      {
        !!userState.userInfo &&
        <div className={styles.container}>
          <TitleInput/>
          <Outline/>
          <Article/>
        </div>
      }
      {
        !userState.userInfo && <LongCreationDemo/>
      }
    </ChatLayout>
  )
})
